// Лимиты бесплатной версии — решение основателя.
//
// Ограничений ровно два: связи и лучики. Всё остальное — совместимость целиком (включая время
// и город), натальная карта, прогноз дня со сбываемостью, статистика — бесплатно и без потолка.
// Подписка снимает оба лимита.

/** Сколько связей можно завести без подписки. Считаются активные + ожидающие приглашения. */
export const FREE_LINK_LIMIT = 5

/** Сколько лучиков можно отправить за сутки без подписки — суммарно, а не на каждую связь. */
export const DAILY_RAY_LIMIT = 5

/**
 * Сколько живёт ссылка-приглашение.
 *
 * Было 14 дней. Стало 30 — по правке основателя «ссылка должна жить точно дольше». Ссылка
 * лежит в чужой переписке, и открывают её не в тот же вечер: сообщение теряется в списке чатов,
 * человек ставит Telegram на паузу, уезжает. Месяц — это «следующий раз, когда я вообще открою
 * этот диалог», и при этом не «никогда»: приглашение занимает слот лимита у пригласившей, и
 * бесконечный срок означал бы вечно занятый слот на человека, который так и не пришёл.
 * Освобождает слот джоб `invite_expire`.
 *
 * Число общее на обе стороны: сервер по нему ставит `expiresAt`, интерфейс по нему пишет
 * «Ссылка живёт 30 дней» в шторке ожидания.
 */
export const INVITE_TTL_DAYS = 30

/**
 * Тратит ли вход по чужому приглашению слот лимита у принимающей стороны.
 *
 * `false` — вход гостевой (спека 04 §2.9): виральная петля не должна упираться в чужой пейволл,
 * поэтому лимит считает **только исходящие** приглашения. Признак вынесен в константу, а не
 * зашит в условие, потому что решение продуктовое и переключается одной строкой: при `true`
 * сервер начнёт отдавать исход `LIMIT_INVITEE`, а интерфейс — показывать его тем же экраном,
 * что и лимит пригласившей.
 */
export const GUEST_LINK_COUNTS_AGAINST_LIMIT: boolean = false

/**
 * По какому времени считаются «сутки» для лимита лучиков.
 *
 * Часового пояса пользователя Telegram нам не отдаёт, а место рождения — это не место жизни.
 * Поэтому одно правило для всех: полночь по UTC+3. Ядро аудитории — СНГ, и для большей её
 * части это и есть местная полночь; для Владивостока счётчик обнулится в 6 утра, что заметно
 * лучше, чем обнуление посреди дня при UTC.
 */
export const RAY_DAY_UTC_OFFSET_HOURS = 3

/** Начало текущих «лучиковых» суток в UTC. Всё, что отправлено позже, попадает в счётчик дня. */
export const getRayDayStart = (now: Date): Date => {
  const shifted = new Date(now.getTime() + RAY_DAY_UTC_OFFSET_HOURS * 3_600_000)
  const midnight = Date.UTC(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate(),
    0,
    0,
    0,
    0,
  )

  return new Date(midnight - RAY_DAY_UTC_OFFSET_HOURS * 3_600_000)
}

/** Когда счётчик обнулится — это же время показывается в интерфейсе («новые лучики в полночь»). */
export const getRayDayEnd = (now: Date): Date =>
  new Date(getRayDayStart(now).getTime() + 24 * 3_600_000)

/**
 * Сколько лучиков осталось сегодня. `null` — без ограничения (подписка).
 *
 * Отдельная функция, а не вычитание на месте: её зовут и бэкенд при отправке, и фронт при
 * отрисовке кнопки, и разъезжаться этим двум нельзя.
 */
export const getRaysLeft = (sentToday: number, isPremium: boolean): number | null => {
  if (isPremium) return null
  return Math.max(0, DAILY_RAY_LIMIT - sentToday)
}

/** Достигнут ли дневной лимит лучиков. */
export const isRayLimitReached = (sentToday: number, isPremium: boolean): boolean =>
  getRaysLeft(sentToday, isPremium) === 0

/** Лимит связей для пользователя: подписка снимает потолок. */
export const getLinkLimit = (linkLimit: number, isPremium: boolean): number | null =>
  isPremium ? null : linkLimit

/** Достигнут ли лимит связей (активные + ожидающие приглашения). */
export const isLinkLimitReached = (used: number, linkLimit: number, isPremium: boolean): boolean =>
  !isPremium && used >= linkLimit
